"use client";

import * as React from "react";
import { useState, useEffect } from "react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Leaf, Layers, Paintbrush } from "lucide-react";
import ProductDetail from "../../ProductDetail/ProductDetail";
export default function HousingList() {
  const [posts, setPosts] = React.useState<any[]>([]);
  const [categories, setCategories] = React.useState<any[]>([]);

  // 雙軌篩選狀態：分別記錄選擇的「系列 ID」與「色系 ID」
  const [selectedSeries, setSelectedSeries] = React.useState<number | null>(null);
  const [selectedColor, setSelectedColor] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(true);

  //  核心修改 1：主狀態完全與網址的 Hash 路由同步控管
  const [activeProductSlug, setActiveProductSlug] = useState<string | null>(null);

  // WordPress 母分類 ID（保留你的設定：82 與 83）
  const SERIES_PARENT_ID = 82;
  const COLOR_PARENT_ID = 83;

  //  核心修改 2：監聽瀏覽器網址變更 (極致優化手機版側滑上一頁體驗)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash && hash.startsWith("#product-")) {
        const slug = hash.replace("#product-", "");
        setActiveProductSlug(slug);
      } else {
        // 當使用者在手機上左滑時，Hash 消失，這裡會自動通知彈窗卸載關閉
        setActiveProductSlug(null);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange(); // 初始化執行

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  //  核心修改 3：開啟與關閉路由控制
  const openProductModal = (slug: string) => {
    window.location.hash = `product-${slug}`;
  };
  const closeProductModal = () => {
    if (window.location.hash) {
      window.history.back(); // 觸發歷史紀錄返回
    }
  };
  React.useEffect(() => {
    const fetchPosts = fetch("/wp-json/wp/v2/flooring?_embed&per_page=100").then(res => res.json());
    const fetchCats = fetch("/wp-json/wp/v2/flooring_cat?per_page=100").then(res => res.json());
    Promise.all([fetchPosts, fetchCats]).then(([postsData, catsData]) => {
      if (Array.isArray(postsData)) setPosts(postsData);
      if (Array.isArray(catsData)) setCategories(catsData);
    }).catch(err => console.error("Error fetching flooring data:", err)).finally(() => setLoading(false));
  }, []);

  // 精準過濾子分類清單
  const seriesCategories = categories.filter(cat => cat.parent === SERIES_PARENT_ID);
  const colorCategories = categories.filter(cat => cat.parent === COLOR_PARENT_ID);

  // 雙軌交集過濾邏輯
  const filteredPosts = posts.filter(post => {
    const matchSeries = selectedSeries === null || post.flooring_cat?.includes(selectedSeries);
    const matchColor = selectedColor === null || post.flooring_cat?.includes(selectedColor);
    return matchSeries && matchColor;
  });
  return <section data-section-id="193524" id="產品展示牆" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/*  核心修改 4：一體化面板優化 ── 移除 bg-[#ffffff] 改為 bg-transparent，移除內部分割線細線、改保留乾淨的外框邊線 */}
        <div className="mb-16 bg-transparent rounded-xl border border-primary/10 shadow-[0_6px_24px_rgba(0,0,0,0.02)] overflow-hidden">
          
          {/* 【第一軌：依設計系列挑選】 ── 移除 bg-primary/[0.01] 鏤空底色 */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center px-8 py-6 lg:py-0 lg:h-24 gap-5 lg:gap-10">
            
            {/* 左側標題區塊 ── 手機下底線 border-b 改為 border-transparent 達成完美全鏤空 */}
            <div className="flex items-center gap-3 w-full lg:w-64 shrink-0 border-b lg:border-b-0 border-transparent pb-3 lg:pb-0 select-none">
              <div className="flex items-center justify-center size-9 rounded-md bg-primary/5 text-primary">
                <Layers className="size-4.5" />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-serif font-semibold tracking-widest text-primary">從喜歡的風格開始</span>
                <span className="text-[10px] font-sans tracking-widest opacity-40 uppercase mt-0.5">SELECT YOUR STYLE</span>
              </div>
            </div>

            {/* 右側大按鈕滑動區 */}
            <div className="w-full flex-1 relative">
              {loading ? <div className="flex gap-3 overflow-x-auto pb-1">
                  {[1, 2, 3].map(i => <div key={i} className="h-11 w-28 bg-muted/40 animate-pulse rounded-full shrink-0" />)}
                </div> : <div className="flex items-center gap-3 overflow-x-auto pb-1 lg:pb-0 scrollbar-hide">
                  <Button variant={selectedSeries === null ? "default" : "outline"} onClick={() => setSelectedSeries(null)} className={`text-sm tracking-widest transition-all duration-300 shrink-0 px-7 h-11 rounded-full ${selectedSeries === null ? "bg-primary text-primary-foreground border-none shadow-sm font-medium" : "border-primary/20 text-primary bg-transparent hover:bg-primary/5 hover:border-primary/40"}`}>
                    全系列
                  </Button>
                  {seriesCategories.map(cat => <Button key={cat.id} variant={selectedSeries === cat.id ? "default" : "outline"} onClick={() => setSelectedSeries(cat.id)} className={`text-sm tracking-widest transition-all duration-300 shrink-0 px-7 h-11 rounded-full ${selectedSeries === cat.id ? "bg-primary text-primary-foreground border-none shadow-sm" : "border-primary/20 text-primary bg-transparent hover:bg-primary/5 hover:border-primary/40"}`}>
                      {cat.name.replace(/木地板/g, '').trim()}
                    </Button>)}
                </div>}
            </div>
          </div>

          {/* 【第二軌：依空間色系挑選】 ── 移除 bg-primary/[0.01]，並上方加上一條極淡裝飾上框線分界兩軌 */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center px-8 py-6 lg:py-0 lg:h-24 gap-5 lg:gap-10 border-t border-primary/5">
            
            {/* 左側標題區塊 */}
            <div className="flex items-center gap-3 w-full lg:w-64 shrink-0 border-b lg:border-b-0 border-transparent pb-3 lg:pb-0 select-none">
              <div className="flex items-center justify-center size-9 rounded-md bg-primary/5 text-primary">
                <Paintbrush className="size-4.5" />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-serif font-semibold tracking-widest text-primary">找到適合家的色調</span>
                <span className="text-[10px] font-sans tracking-widest opacity-40 uppercase mt-0.5">SELECT YOUR TONE</span>
              </div>
            </div>

            {/* 右側大按鈕滑動區 */}
            <div className="w-full flex-1 relative">
              {loading ? <div className="flex gap-3 overflow-x-auto pb-1">
                  {[1, 2, 3, 4].map(i => <div key={i} className="h-11 w-28 bg-muted/40 animate-pulse rounded-full shrink-0" />)}
                </div> : <div className="flex items-center gap-3 overflow-x-auto pb-1 lg:pb-0 scrollbar-hide">
                  <Button variant={selectedColor === null ? "default" : "outline"} onClick={() => setSelectedColor(null)} className={`text-sm tracking-widest transition-all duration-300 shrink-0 px-7 h-11 rounded-full ${selectedColor === null ? "bg-primary text-primary-foreground border-none shadow-sm font-medium" : "border-primary/20 text-primary bg-transparent hover:bg-primary/5 hover:border-primary/40"}`}>
                    全色系
                  </Button>
                  {colorCategories.map(cat => <Button key={cat.id} variant={selectedColor === cat.id ? "default" : "outline"} onClick={() => setSelectedColor(cat.id)} className={`text-sm tracking-widest transition-all duration-300 shrink-0 px-7 h-11 rounded-full ${selectedColor === cat.id ? "bg-primary text-primary-foreground border-none shadow-sm" : "border-primary/20 text-primary bg-transparent hover:bg-primary/5 hover:border-primary/40"}`}>
                      {cat.name.replace(/木地板/g, '').trim()}
                    </Button>)}
                </div>}
            </div>
          </div>

        </div>

        {/* 產品展示網格 */}
        {loading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2].map(i => <div key={i} className="h-[450px] bg-muted/20 animate-pulse rounded-lg border border-border" />)}
          </div> : filteredPosts.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {filteredPosts.map((post, index) => <Card key={post.id} data-index={index} className="group overflow-hidden border-none shadow-none bg-transparent transition-all duration-500 animate-in fade-in zoom-in duration-700">
                {/* 產品大圖區 */}
                <div className="relative overflow-hidden rounded-[4px] cursor-pointer"
          //  核心修改 5：點擊改調用自訂的 Hash 開啟機制
          onClick={() => openProductModal(post.slug)}>
                  <AspectRatio ratio={16 / 10}>
                    <img src={post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618354/img/flooring_product_fallback.png"} alt={post.title.rendered.replace(/<[^>]+>/g, '')} className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-105 hover:brightness-95" />
                  </AspectRatio>
                  <div className="absolute inset-0 border border-primary/5 pointer-events-none" />
                </div>
                
                {/* 產品文案內容 */}
                <CardContent className="pt-8 pb-4 flex flex-col items-center gap-4">
                  <h3 className="text-[1.6rem] font-serif font-semibold text-primary leading-tight text-center tracking-wider transition-colors duration-500 group-hover:text-primary">
                    {post.title.rendered}
                  </h3>
                  <div className="h-px bg-primary/20 w-12 mx-auto transition-all duration-500 group-hover:w-24" />
                  <div className="text-muted-foreground text-sm font-sans leading-relaxed line-clamp-2 text-center max-w-[80%]" dangerouslySetInnerHTML={{
              __html: post.content.rendered
            }} />
                </CardContent>
              </Card>)}
          </div> : (/* 空狀態提示 */
      <div className="py-32 text-center flex flex-col items-center justify-center gap-4 animate-in fade-in duration-700">
            <Leaf className="size-12 text-primary/20 animate-bounce" />
            <p className="text-muted-foreground text-base font-sans tracking-widest">
              沒有同時符合此系列與色系的地板產品，請嘗試重設篩選條件。
            </p>
          </div>)}

        {/*  核心修改 6：移除原本寫在這裡長串重複的全螢幕 HTML 外殼結構，改直接調用 ProductDetail，由內核處理彈窗跟防滾動 */}
        <ProductDetail slug={activeProductSlug} onClose={closeProductModal} />
        
      </div>
    </section>;
}